import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { PERMISSIONS } from '@/types/permissions';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredResource?: string;
  requiredAction?: string;
  requiredRole?: 'admin' | 'financeiro' | 'representante' | 'parceiro' | 'vendedor' | Array<'admin' | 'financeiro' | 'representante' | 'parceiro' | 'vendedor'>;
  blockedRole?: 'admin' | 'financeiro' | 'representante' | 'parceiro' | 'vendedor' | Array<'admin' | 'financeiro' | 'representante' | 'parceiro' | 'vendedor'>;
  fallbackPath?: string;
  showUnauthorizedMessage?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredResource,
  requiredAction,
  requiredRole,
  blockedRole,
  fallbackPath = '/unauthorized',
  showUnauthorizedMessage = true,
}) => {
  const { user, loading: authLoading } = useAuth();
  
  // Usar useAuthorization de forma defensiva
  let authz;
  try {
    authz = useAuthorization();
  } catch (e) {
    console.error("❌ ProtectedRoute: Erro ao obter contexto de autorização:", e);
    authz = null;
  }

  const { userRole, userProfile, isLoading: authzLoading, can, canAccess, hasRole } = authz || {
    userRole: null,
    userProfile: null,
    isLoading: false,
    can: () => false,
    canAccess: () => false,
    hasRole: () => false
  };

  const location = useLocation();

  // Nota: Verificação de senha provisória movida para o componente Auth
  // para evitar conflitos de redirecionamento

  // Aguardar carregamento da autenticação e autorização
  // Se já temos o usuário e o perfil, não mostramos o loading para evitar 
  // que o componente filho seja desmontado durante atualizações em segundo plano
  if ((authLoading || authzLoading) && (!user || !userProfile)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Verificar se o usuário está autenticado
  if (!user || !userProfile) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Verificar contrato de plataforma assinado (obrigatório para parceiro e vendedor)
  const precisaContratoPlataforma = userRole === 'parceiro' || userRole === 'vendedor';
  const contratoAssinado = !!(userProfile as { contrato_plataforma_assinado_em?: string | null })?.contrato_plataforma_assinado_em;
  if (precisaContratoPlataforma && !contratoAssinado) {
    return <Navigate to="/assinatura-contrato-plataforma" state={{ from: location }} replace />;
  }

  // Verificar se o usuário está ativo
  if (!userProfile.ativo) {
    // Se for parceiro, mostrar mensagem específica para ativação
    if (userProfile.role === 'parceiro') {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Conta Não Ativada</h2>
            <p className="text-gray-600 mb-4">
              Olá, <strong>{userProfile.nome}</strong>! Sua conta de parceiro ainda não foi ativada.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Você deve ter recebido um e-mail com instruções para definir sua senha e ativar sua conta.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/auth'}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Verificar E-mail
              </button>
              <button
                onClick={() => window.location.href = '/contato'}
                className="w-full px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 transition-colors"
              >
                Entrar em Contato
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Para outros tipos de usuário, mostrar mensagem padrão
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Usuário Inativo</h2>
          <p className="text-gray-600 mb-4">
            Sua conta foi desativada. Entre em contato com o administrador.
          </p>
          <button
            onClick={() => window.location.href = '/auth'}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Voltar ao Login
          </button>
        </div>
      </div>
    );
  }

  // Verificar permissão específica
  if (requiredPermission && !can(requiredPermission)) {
    if (showUnauthorizedMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h2>
            <p className="text-gray-600 mb-4">
              Você não tem permissão para acessar este recurso.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Permissão necessária: {requiredPermission}
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Verificar acesso a recurso
  if (requiredResource && requiredAction && !canAccess(requiredResource, requiredAction)) {
    if (showUnauthorizedMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h2>
            <p className="text-gray-600 mb-4">
              Você não tem permissão para {requiredAction} em {requiredResource}.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Verificar papel específico bloqueado
  if (blockedRole) {
    const blockedRoles = Array.isArray(blockedRole) ? blockedRole : [blockedRole];
    const hasBlockedRole = blockedRoles.some(role => hasRole(role));
    
    if (hasBlockedRole) {
      if (showUnauthorizedMessage) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h2>
              <p className="text-gray-600 mb-4">
                Esta funcionalidade não está disponível para o perfil de {userRole}.
              </p>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Voltar
              </button>
            </div>
          </div>
        );
      }
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // Verificar papel específico
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasAllowedRole = allowedRoles.some(role => hasRole(role));
    
    if (!hasAllowedRole) {
      if (showUnauthorizedMessage) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h2>
              <p className="text-gray-600 mb-4">
                Esta funcionalidade é restrita a usuários com perfil de {allowedRoles.join(' ou ')}.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Seu perfil atual: {userRole}
              </p>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Voltar
              </button>
            </div>
          </div>
        );
      }
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // Se todas as verificações passaram, renderizar o conteúdo
  return <>{children}</>;
};

// Componentes de conveniência para diferentes níveis de acesso
export const AdminOnly: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRole="admin">{children}</ProtectedRoute>
);

export const RepresentativeOnly: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRole="representante">{children}</ProtectedRoute>
);

export const PartnerOnly: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRole="parceiro">{children}</ProtectedRoute>
);

// Componentes para verificação de permissões específicas
export const LeadsManagement: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermission={PERMISSIONS.LEADS.MANAGE}>{children}</ProtectedRoute>
);

export const ClientsManagement: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermission={PERMISSIONS.CLIENTS.MANAGE}>{children}</ProtectedRoute>
);

export const ContractsManagement: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermission={PERMISSIONS.CONTRACTS.MANAGE}>{children}</ProtectedRoute>
);

export const UsersManagement: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermission={PERMISSIONS.USERS.MANAGE}>{children}</ProtectedRoute>
);

export const ReportsAccess: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermission={PERMISSIONS.REPORTS.READ}>{children}</ProtectedRoute>
);

export const SettingsAccess: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermission={PERMISSIONS.SETTINGS.READ}>{children}</ProtectedRoute>
);

